import React, { useMemo, useState, useEffect } from "react";
import {
  Col,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  DatePicker,
  Checkbox,
  Divider,
  Spin,
  Modal,
} from "antd";
import type { FormInstance } from "antd/es/form/Form";
import type { Moment } from "moment";
import NumberFormat from "react-number-format";
import { useAppSelector } from "../../../..";
import { checkIsLoading, displayFormat, getSelectOptions } from "../../../../lib/utils";
import { ReactComponent as CancelButtonIcon } from "../../../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../../../img/buttons_icons/submitButton_icon.svg";
import {
  Agreement,
  IChangingMode,
  ICreateMode,
  Offer,
  IOfferFieldName,
} from "../../../../lib/types";
import {
  ICreatingFormsData,
  IOfferListSelectMode,
  IOfferParamsFormData,
} from "../../../../containers/OfferContainer/context/types";
import { isFieldAvailable } from "./utils/isFieldAvailable";
import { isFieldRequired } from "./utils/isFieldRequired";
import { OFFER_STATUSES } from "../../../../store/slices/offering/const";
import { DictionaryDto, Property } from "../../../../lib/interfaces";
import { IOfferListSearch } from "../../../../store/slices/offering/types";
import { lifespanPeriodIdsWithoutQty } from "../../../../lib/const/lifespanPeriodIdsWithoutQty";
import "./ProductOfferParamsEdit.less";

const formItemLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
  className: "form-item-required__hidden-error__mb0",
};

const rowLayout = {
  gutter: 50,
  style: {
    marginTop: "10px",
  },
};

interface ComponentProps {
  offer?: Offer<Moment, "createForm">;
  viewMode: IChangingMode | ICreateMode;
  onCancel?: () => void;
  onSubmit?: (mode: IChangingMode) => void;
  isLoading?: boolean;
  onChangeExtName?: (extName: string) => void;
  onChangeOfferingTypeId?: (id: number) => void;
  onChangeTariffModelId: (id: number) => void;
  creatingFormsData?: ICreatingFormsData;
  onNameBlur: (name?: string) => Promise<boolean>;
  allowedOfferingStatuses: DictionaryDto[];
  form: FormInstance<IOfferParamsFormData>;
  searchListValues: IOfferListSearch;
  offerListSelectMode?: IOfferListSelectMode;
  disabledFields?: IOfferFieldName[]; // Для частных случаев, общие правила определены в isFieldAvailable
  selectedAgreement?: Agreement;
  isRoot?: boolean;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ProductOfferParamsEdit = (props: ComponentProps) => {
  const {
    offer,
    viewMode,
    onCancel,
    onSubmit,
    isLoading = false,
    onChangeOfferingTypeId,
    onChangeTariffModelId,
    creatingFormsData,
    onNameBlur,
    allowedOfferingStatuses,
    form,
    searchListValues,
    offerListSelectMode,
    disabledFields = [],
    isRoot,
  } = props;
  const {
    offeringAccessTypes,
    offeringAccessTypesLoading,
    offeringStatusesLoading,
    offeringProlongTypes,
    offeringProlongTypesLoading,
    lifespanPeriods,
    lifespanPeriodsLoading,
    offeringBundleTypes,
    offeringBundleTypesLoading,
  } = useAppSelector((state) => state.dicts);

  const {
    tariffPeriodAlignments,
    tariffPeriodAlignmentsLoading,
    tariffModelTypes,
    tariffModelTypesLoading,
  } = useAppSelector((state) => state.rtkDictionaries);

  const { serviceTypeList, offerHierarchy } = useAppSelector((state) => state.offering);

  const tenantPropertiesDto = useAppSelector(
    (state) => state.tenants.tenantPropertiesDto
  ) as Property[];

  // Обязательность поля договор, в режиме создания версии зависит от измененного статуса
  const [addVersionStatusId, setAddVersionStatusId] = useState<OFFER_STATUSES>();

  useEffect(() => {
    if (viewMode === "add" && offer) {
      setAddVersionStatusId(offer.offeringStatusId);
    }
  }, [offer, viewMode]);

  const offeringStatusId = useMemo(() => {
    if (viewMode === "create") return OFFER_STATUSES.PREPARE;
    return offer?.offeringStatusId;
  }, [viewMode, offer]);

  const getRequired = (fieldName: IOfferFieldName): boolean => {
    if (offerListSelectMode === "select") return false;
    if (disabledFields.includes(fieldName)) return false;
    if (offeringStatusId) {
      return isFieldRequired({
        fieldName,
        viewMode,
        offeringStatusId,
        options: {
          offeringTypeId: creatingFormsData?.offer.offeringTypeId || offer?.offeringTypeId,
          addVersionStatusId,
        },
      });
    }
    return false;
  };

  const getAvailable = (fieldName: IOfferFieldName): boolean => {
    if (offerListSelectMode === "select") return false;
    if (disabledFields.includes(fieldName)) return false;
    if (offeringStatusId) {
      return isFieldAvailable({
        fieldName,
        viewMode,
        offeringStatusId,
        options: {
          offeringTypeId: creatingFormsData?.offer.offeringTypeId || offer?.offeringTypeId,
          addVersionStatusId,
          selectedServiceTypeId: searchListValues.serviceTypeId,
          isCreateBasedOn: offerListSelectMode === "basedOn",
          isRoot,
        },
      });
    }
    return false;
  };

  // Id типов "индивидуальный" предложения и профиля не совпадают, для этого синхронизация. Только в режиме создания
  const handleChangeOfferingTypeId = (id: number) => {
    onChangeOfferingTypeId && onChangeOfferingTypeId(id);
  };

  const disableFromDate = (date: Moment): boolean => {
    if (viewMode === "create") {
      const toDate = form.getFieldValue("toDate");
      return toDate ? date.isAfter(toDate, "day") : false;
    }
    return !date.isBetween(offer?.fromDate, offer?.toDate, "day", "(]");
  };

  const disableToDate = (date: Moment): boolean => {
    if (viewMode === "create") {
      const fromDate = form.getFieldValue("fromDate");
      return fromDate ? date.isBefore(fromDate, "day") : false;
    }
    return !date.isBetween(offer?.fromDate, offer?.toDate, "day", "[]");
  };

  const handleOnCancel = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    onCancel && onCancel();
  };

  const handleOnSubmit = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    viewMode !== "create" && onSubmit && onSubmit(viewMode);
  };

  const renderButtons = () =>
    viewMode !== "create" ? (
      <Row align="middle" className="product-offer-params-edit__btns">
        <Col>
          <SubmitButtonIcon onClick={handleOnSubmit} />
        </Col>
        <Col>
          <CancelButtonIcon onClick={handleOnCancel} />
        </Col>
      </Row>
    ) : null;

  return (
    <div className="product-offer-params-edit">
      <Spin spinning={isLoading}>
        <Divider style={{ margin: "0 0 9px" }} />
        <Row {...rowLayout}>
          <Col span={6}>
            <Form.Item
              label="Наименование"
              name="extName"
              rules={[{ required: getRequired("extName"), message: "" }]}
              {...formItemLayout}
            >
              <Input allowClear disabled={!getAvailable("extName")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Тип пролонгации"
              name="prolongTypeId"
              {...formItemLayout}
              rules={[{ required: getRequired("prolongTypeId"), message: "" }]}
            >
              <Select
                loading={checkIsLoading(offeringProlongTypesLoading)}
                options={getSelectOptions({
                  list: offeringProlongTypes,
                })}
                disabled={!getAvailable("prolongTypeId")}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Тип"
              name="offeringTypeId"
              {...formItemLayout}
              rules={[{ required: getRequired("offeringTypeId"), message: "" }]}
            >
              <Select
                loading={checkIsLoading(offeringAccessTypesLoading)}
                options={getSelectOptions({
                  list: offeringAccessTypes,
                })}
                onSelect={handleChangeOfferingTypeId}
                disabled={!getAvailable("offeringTypeId")}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Статус"
              name="offeringStatusId"
              {...formItemLayout}
              rules={[{ required: getRequired("offeringStatusId"), message: "" }]}
              labelCol={{ span: 9 }}
              wrapperCol={{ span: 9 }}
            >
              <Select
                disabled={!getAvailable("offeringStatusId")}
                loading={checkIsLoading(offeringStatusesLoading)}
                options={getSelectOptions({
                  list: allowedOfferingStatuses,
                })}
              />
            </Form.Item>
          </Col>
          {renderButtons()}
        </Row>
        <Row {...rowLayout}>
          <Col span={6}>
            <Form.Item
              label="Код"
              name="name"
              rules={[{ required: getRequired("name"), message: "" }]}
              {...formItemLayout}
            >
              <Input
                allowClear
                disabled={!getAvailable("name")}
                onBlur={(e) =>
                  onNameBlur(e.target.value).then((result) => {
                    if (result === false) {
                      form.setFields([
                        {
                          name: "name",
                          errors: [""],
                        },
                      ]);
                    }
                  })
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Row gutter={5}>
              <Col span={20}>
                <Form.Item
                  label="Период продукта"
                  name="lifespanPeriodId"
                  rules={[{ required: getRequired("lifespanPeriodId"), message: "" }]}
                  {...formItemLayout}
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                >
                  <Select
                    allowClear
                    options={getSelectOptions({
                      list: lifespanPeriods,
                    })}
                    loading={checkIsLoading(lifespanPeriodsLoading)}
                    disabled={!getAvailable("lifespanPeriodId")}
                    onSelect={(id: number) => {
                      if (lifespanPeriodIdsWithoutQty.includes(id)) {
                        form.setFieldsValue({ lifespanPeriodQty: 1 });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Tooltip title="Количество: целое от 1 до 30000">
                  <Form.Item noStyle dependencies={["lifespanPeriodId"]}>
                    {({ getFieldValue }) => {
                      const lifespanPeriodId = getFieldValue("lifespanPeriodId");
                      return (
                        <Form.Item
                          name="lifespanPeriodQty"
                          rules={[
                            { required: getRequired("lifespanPeriodQty"), message: "" },
                            {
                              validator: (_, qty) => {
                                const isQtyValid = qty >= 1 && qty <= 30000;
                                const isInteger = Number.isInteger(Number(qty));
                                const withoutE = !String(qty).includes("e");
                                if (isQtyValid && isInteger && withoutE) {
                                  return Promise.resolve();
                                }
                                return Promise.reject();
                              },
                              message: "",
                            },
                          ]}
                          {...formItemLayout}
                          wrapperCol={{ span: 24 }}
                        >
                          <NumberFormat
                            className="ant-input-affix-wrapper inputNumber__hideArrows"
                            decimalScale={0}
                            fixedDecimalScale
                            allowNegative={false}
                            disabled={
                              !getAvailable("lifespanPeriodQty") ||
                              lifespanPeriodIdsWithoutQty.includes(lifespanPeriodId)
                            }
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Tooltip>
              </Col>
            </Row>
          </Col>
          <Col span={6} offset={6}>
            <Form.Item
              label="Доступ к подкл."
              name="isStandalone"
              {...formItemLayout}
              labelCol={{ span: 9 }}
              wrapperCol={{ span: 9 }}
              valuePropName="checked"
            >
              <Checkbox disabled={!getAvailable("isStandalone")} />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={6}>
            <Form.Item shouldUpdate className="form-item-required__hidden-error__mb0">
              {({ getFieldValue, setFieldsValue }) => {
                const bundleTypeId = getFieldValue("bundleTypeId");
                const onSelectBundleTypeId = (id: number) => {
                  if (
                    bundleTypeId !== 1 &&
                    id === 1 &&
                    offerHierarchy?.children?.length &&
                    (viewMode === "edit" || viewMode === "add")
                  ) {
                    setFieldsValue({ bundleTypeId });
                    Modal.error({
                      content: "Необходимо закрыть или удалить связанные Предложения",
                      width: "500px",
                    });
                  }
                };
                return (
                  <Form.Item
                    label="Тип пакета"
                    name="bundleTypeId"
                    {...formItemLayout}
                    rules={[{ required: getRequired("bundleTypeId"), message: "" }]}
                  >
                    <Select
                      disabled={!getAvailable("bundleTypeId")}
                      options={getSelectOptions({
                        list: offeringBundleTypes,
                      })}
                      loading={checkIsLoading(offeringBundleTypesLoading)}
                      showArrow={getAvailable("bundleTypeId")}
                      onSelect={onSelectBundleTypeId}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              dependencies={["tariffModelId"]}
              className="form-item-required__hidden-error__mb0"
            >
              {() => (
                <Form.Item
                  label="Смещение периода"
                  name="periodAlignmentId"
                  {...formItemLayout}
                  rules={[{ required: getRequired("periodAlignmentId"), message: "" }]}
                >
                  <Select
                    disabled={!getAvailable("periodAlignmentId")}
                    options={getSelectOptions({
                      list: tariffPeriodAlignments,
                    })}
                    loading={checkIsLoading(tariffPeriodAlignmentsLoading)}
                    showArrow={getAvailable("periodAlignmentId")}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
          <Col span={6} offset={6}>
            <Form.Item
              label="Дата создания"
              name="fromDate"
              rules={[{ required: getRequired("fromDate"), message: "" }]}
              {...formItemLayout}
              labelCol={{ span: 9 }}
              wrapperCol={{ span: 9 }}
            >
              <DatePicker
                format={displayFormat}
                disabled={!getAvailable("fromDate")}
                disabledDate={disableFromDate}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row {...rowLayout}>
          <Col span={6} offset={6}>
            <Form.Item
              label="Бизнес-модель"
              name="tariffModelId"
              {...formItemLayout}
              rules={[{ required: getRequired("tariffModelId"), message: "" }]}
            >
              <Select
                disabled={!getAvailable("tariffModelId")}
                options={getSelectOptions({
                  list: tariffModelTypes,
                })}
                loading={checkIsLoading(tariffModelTypesLoading)}
                showArrow={getAvailable("tariffModelId")}
                onChange={onChangeTariffModelId}
              />
            </Form.Item>
          </Col>
          <Col span={6} offset={6}>
            <Form.Item
              label="Дата окончания"
              name="toDate"
              rules={[{ required: getRequired("toDate"), message: "" }]}
              {...formItemLayout}
              labelCol={{ span: 9 }}
              wrapperCol={{ span: 9 }}
            >
              <DatePicker
                format={displayFormat}
                disabled={!getAvailable("toDate")}
                disabledDate={disableToDate}
              />
            </Form.Item>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};
